/**
 * PageToolbar — second chrome row below the AppShell header.
 * Sticky, carries: back link · page title · primary actions ·
 * secondary actions (desktop-only) · overflow menu.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { OverflowMenu, type OverflowMenuItem } from './primitives';

export interface PageToolbarProps {
  back?: { to: string; label: string };
  title: string;
  primary?: ReactNode;
  secondary?: ReactNode;
  overflow?: OverflowMenuItem[];
}

export function PageToolbar({ back, title, primary, secondary, overflow }: PageToolbarProps) {
  const hasTrailing = primary || secondary || (overflow && overflow.length > 0);

  return (
    <div className="sticky top-14 z-20 border-b border-border bg-surface/95 backdrop-blur-sm">
      <div className="mx-auto flex max-w-7xl items-center gap-2 px-4 py-2.5 sm:gap-3 sm:px-6 sm:py-3">
        {back && (
          <Link
            to={back.to}
            className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-small text-ink-muted transition-colors hover:bg-surface-raised hover:text-ink"
          >
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
            <span className="hidden sm:inline">{back.label}</span>
          </Link>
        )}
        <h1 className="flex-1 truncate text-h2 font-semibold text-ink">{title}</h1>
        {hasTrailing && (
          <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
            {primary}
            {secondary && <div className="hidden sm:contents">{secondary}</div>}
            {overflow && overflow.length > 0 && (
              <OverflowMenu items={overflow} align="right" />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
